import React, { useState } from 'react';
import { FiLock } from 'react-icons/fi';
import PaymentStep from '../../../../components/QuestionTypes/PaymentStep';

function PolicyModal({ policyDoc, onClose, onAgree }) {
  if (!policyDoc) return null;
  return (
    <div className="lcw-modal-backdrop" onClick={onClose}>
      <div className="lcw-modal" onClick={e => e.stopPropagation()}>
        <div className="lcw-modal-header">
          <div>
            <div className="lcw-modal-title">{policyDoc.title}</div>
            <span className="lcw-modal-key">
              {policyDoc.version ? `Version ${policyDoc.version}` : policyDoc.policy_type}
            </span>
          </div>
          <button className="lcw-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="lcw-modal-body">{policyDoc.content || ""}</div>
        <div className="lcw-modal-footer">
          <button className="lcw-modal-action lcw-modal-action--secondary" onClick={onClose}>Close</button>
          <button className="lcw-modal-action lcw-modal-action--primary" onClick={onAgree}>Agree &amp; check</button>
        </div>
      </div>
    </div>
  );
}

export default function StepPay({
  lab, selectedMethod, total, subtotal,
  couponCode, setCouponCode, appliedCoupon, couponError, couponLoading,
  onApplyCoupon, onRemoveCoupon,
  consentHipaa, setConsentHipaa,
  consentTerms, setConsentTerms,
  consentTele, setConsentTele,
  paymentConfig, paymentConfigLoading,
  savedPaymentMethods, savedPaymentMethodsLoading,
  paymentStepRef,
  checkoutPolicies, checkoutPoliciesLoading, checkoutPoliciesMissing,
  isSubmitting, formError, onPlaceOrder,
}) {
  const [openPolicy, setOpenPolicy] = useState(null);

  const agreeAndClose = () => {
    if (openPolicy === 'hipaa') setConsentHipaa(true);
    if (openPolicy === 'terms') setConsentTerms(true);
    if (openPolicy === 'tele') setConsentTele(true);
    setOpenPolicy(null);
  };

  const methodLabel = selectedMethod?.label || selectedMethod?.collection_method_display || '';
  const paymentRequired = total > 0;
  const activeGateway = paymentConfig?.active_gateway || paymentConfig?.gateway;
  const paymentGatewayReady =
    !paymentRequired ||
    (activeGateway === 'stripe'
      ? Boolean(paymentConfig?.stripe_publishable_key || paymentConfig?.stripe?.publishable_key)
      : activeGateway === 'nmi'
        ? Boolean(paymentConfig?.nmi_client_key || paymentConfig?.nmi_public_key)
        : activeGateway === 'authorize_net'
          ? Boolean(paymentConfig?.authorize_net_client_key && paymentConfig?.authorize_net_api_login)
          : Boolean(activeGateway));
  const missingPolicies = Array.isArray(checkoutPoliciesMissing) ? checkoutPoliciesMissing : [];
  const policyDocs = {
    hipaa: checkoutPolicies?.hipaa,
    terms: checkoutPolicies?.terms,
    tele: checkoutPolicies?.telehealth,
  };
  return (
    <div className="lcw-card">
      {/* Order recap */}
      <div className="lcw-card-body">
        <div className="lcw-review">
          <div>
            <div className="lcw-review-name">{lab.name}</div>
            {methodLabel && <div className="lcw-review-sub">{methodLabel}</div>}
            <div className="lcw-review-sub lcw-review-sub--mono">
              Junction order: {selectedMethod?.assignment_id || lab.id || '...'}
            </div>
          </div>
          <div className="lcw-review-price">${subtotal.toFixed(2)}</div>
        </div>
      </div>

      <div className="lcw-card-divider" />

      {/* Coupon */}
      <div className="lcw-card-body">
        {appliedCoupon ? (
          <div className="lcw-coupon-applied">
            <span className="lcw-coupon-code">{appliedCoupon.code}</span>
            <button className="lcw-coupon-remove" onClick={onRemoveCoupon}>Remove</button>
          </div>
        ) : (
          <>
            <div className="lcw-coupon-row">
              <input
                className="lcw-coupon-input"
                value={couponCode}
                onChange={e => setCouponCode(e.target.value)}
                placeholder="Coupon code"
                onKeyDown={e => { if (e.key === 'Enter') onApplyCoupon(); }}
              />
              <button className="lcw-coupon-btn" onClick={onApplyCoupon} disabled={couponLoading || !couponCode.trim()}>
                {couponLoading ? '…' : 'Apply'}
              </button>
            </div>
            {couponError && <div className="lcw-inline-error">{couponError}</div>}
          </>
        )}
      </div>

      <div className="lcw-card-divider" />

      {/* Consents */}
      <div className="lcw-card-body">
        <div className="lcw-consents-title">Consents</div>
        <p className="lcw-consents-sub">Required before your lab order can be processed.</p>
        {checkoutPoliciesLoading ? (
          <div className="lcw-spinner">Loading policy content…</div>
        ) : null}
        {missingPolicies.length > 0 ? (
          <div className="lcw-error">Required policy content is unavailable for this checkout.</div>
        ) : null}

        {[
          { key: 'hipaa', label: 'HIPAA Authorization', desc: 'I authorize the use and disclosure of my health information for this lab order.', checked: consentHipaa, set: setConsentHipaa },
          { key: 'terms', label: 'Terms of Use', desc: 'I agree to the terms of use and privacy policy.', checked: consentTerms, set: setConsentTerms },
          { key: 'tele', label: 'Telehealth Informed Consent', desc: 'I consent to a telehealth evaluation by a licensed physician for this order.', checked: consentTele, set: setConsentTele },
        ].map(({ key, label, desc, checked, set }) => (
          <div key={key} className="lcw-consent-row">
            <input
              type="checkbox"
              className="lcw-consent-check"
              checked={checked}
              onChange={e => set(e.target.checked)}
              id={`consent-${key}`}
              disabled={checkoutPoliciesLoading || !policyDocs[key]}
            />
            <label className="lcw-consent-label" htmlFor={`consent-${key}`}>
              <span className="lcw-consent-title">{label}</span><br />
              <span className="lcw-consent-desc">
                {desc}{' '}
                <button
                  type="button"
                  className="lcw-read-policy"
                  onClick={e => { e.preventDefault(); setOpenPolicy(key); }}
                  disabled={!policyDocs[key]}
                >
                  Read policy
                </button>
              </span>
            </label>
          </div>
        ))}
      </div>

      <div className="lcw-card-divider" />

      {/* Payment */}
      <div className="lcw-card-body">
        <div className="lcw-payment-title">Payment</div>
        <p className="lcw-payment-sub">No insurance needed. Your payment is captured after the lab order is accepted.</p>

        {!paymentRequired ? (
          <div className="lcw-info">Your coupon covers the full amount — no payment needed.</div>
        ) : paymentConfigLoading || savedPaymentMethodsLoading ? (
          <div className="lcw-spinner">Loading secure payment…</div>
        ) : paymentConfig && paymentGatewayReady ? (
          <PaymentStep
            ref={paymentStepRef}
            question={{ id: 'payment' }}
            mode="checkout"
            checkoutPaymentConfig={paymentConfig}
            checkoutSavedPaymentMethods={savedPaymentMethods}
            checkoutOrderSummary={{ amount: total }}
            hideCheckoutOrderSummary
          />
        ) : paymentConfig && !paymentGatewayReady ? (
          <div className="lcw-error">
            Payment configuration is incomplete for {activeGateway || 'the active gateway'}. Please contact support.
          </div>
        ) : (
          <div className="lcw-error">Payment configuration not available.</div>
        )}

        {/* Security note placed at bottom of payment body matching prototype */}
        <div className="lcw-security-note">
          <FiLock size={13} />
          <span>HIPAA-secure checkout. Your payment is captured only after your lab order is accepted.</span>
        </div>

        {formError && <div className="lcw-error">{formError}</div>}
      </div>

      {openPolicy && (
        <PolicyModal policyDoc={policyDocs[openPolicy]} onClose={() => setOpenPolicy(null)} onAgree={agreeAndClose} />
      )}
    </div>
  );
}
